import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState, type DragEvent } from 'react';

import { ApiError } from '../api/client.js';
import { customersApi } from '../api/customers.api.js';
import { SLOTS_BUSINESS, SLOTS_PERSONAL } from './document-types.js';

/**
 * File many documents at once — chosen together, as a folder, or dropped in —
 * with progress and a result for each file (DEV-66).
 *
 * Documents used to go in one at a time, which on a business file with a dozen
 * papers was most of an afternoon. Each file is still its own request, so one
 * that fails (too big, wrong type, a dropped connection) is reported against
 * that file and leaves the rest filed; it can be retried on its own.
 */

/** Mirrors what the gateway accepts; the server still decides. */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = /\.(pdf|jpe?g|png|heic|webp)$/i;
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.heic,.webp';
/** One batch stays well inside the per-person request limit (100 a minute). */
export const MAX_FILES = 30;
const CONCURRENCY = 3;


type ItemState = 'ready' | 'skipped' | 'uploading' | 'filed' | 'failed';

interface Item {
  readonly key: string;
  readonly file: File;
  readonly slot: string;
  readonly state: ItemState;
  readonly progress: number;
  readonly message: string;
}

/** Why a file will not be sent, or null when it may be. */
function precheck(file: File): string | null {
  if (!ACCEPTED.test(file.name)) return 'Not sent: only PDF, JPG, PNG, HEIC or WEBP files are accepted.';
  if (file.size === 0) return 'Not sent: the file is empty.';
  if (file.size > MAX_BYTES) return 'Not sent: larger than the 10 MB limit.';
  return null;
}

function sizeLabel(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

function failureMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 429) return 'Too many uploads in a minute. Retry in a moment.';
  return err instanceof Error ? err.message : 'The file could not be filed.';
}

/** Every file under a dropped folder, however deep. */
async function filesFromEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise((resolve) => (entry as FileSystemFileEntry).file((f) => resolve([f]), () => resolve([])));
  }
  if (!entry.isDirectory) return [];
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  const children: FileSystemEntry[] = [];
  // readEntries returns the listing in chunks until it returns none.
  for (;;) {
    const chunk = await new Promise<FileSystemEntry[]>((resolve) => reader.readEntries(resolve, () => resolve([])));
    if (chunk.length === 0) break;
    children.push(...chunk);
  }
  return (await Promise.all(children.map(filesFromEntry))).flat();
}

interface Props {
  readonly customerId: string;
  readonly isBusiness: boolean;
  /** Called once a batch has filed at least one document, to refresh the list. */
  readonly onUploaded: () => void;
}

export function BulkDocumentUpload({ customerId, isBusiness, onUploaded }: Props): JSX.Element {
  const slots = isBusiness ? SLOTS_BUSINESS : SLOTS_PERSONAL;
  const defaultSlot = slots[0]?.[0] ?? 'company';
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<readonly Item[]>([]);
  const [running, setRunning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const counter = useRef(0);

  const update = (key: string, change: Partial<Item>): void =>
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...change } : item)));

  const add = (files: readonly File[]): void => {
    setItems((prev) => {
      const seen = new Set(prev.map((item) => `${item.file.name}:${String(item.file.size)}:${String(item.file.lastModified)}`));
      let sendable = prev.filter((item) => item.state !== 'skipped').length;
      const added: Item[] = [];
      for (const file of files) {
        // System files a folder brings along (.DS_Store, Thumbs.db) are not paperwork.
        if (file.name.startsWith('.') || file.name === 'Thumbs.db') continue;
        const id = `${file.name}:${String(file.size)}:${String(file.lastModified)}`;
        if (seen.has(id)) continue;
        seen.add(id);
        let problem = precheck(file);
        if (problem === null && sendable >= MAX_FILES) problem = `Not sent: one batch takes up to ${String(MAX_FILES)} files.`;
        if (problem === null) sendable++;
        added.push({
          key: `f${String(counter.current++)}`, file, slot: defaultSlot,
          state: problem === null ? 'ready' : 'skipped', progress: 0, message: problem ?? '',
        });
      }
      return [...prev, ...added];
    });
  };

  const onDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (running) return;
    const entries = [...event.dataTransfer.items]
      .map((item) => item.webkitGetAsEntry?.() ?? null)
      .filter((entry): entry is FileSystemEntry => entry !== null);
    if (entries.length === 0) {
      add([...event.dataTransfer.files]);
      return;
    }
    void Promise.all(entries.map(filesFromEntry)).then((lists) => add(lists.flat()));
  };

  const run = async (): Promise<void> => {
    const queue = items.filter((item) => item.state === 'ready' || item.state === 'failed');
    if (queue.length === 0) return;
    setRunning(true);
    let filed = 0;
    let next = 0;
    const worker = async (): Promise<void> => {
      while (next < queue.length) {
        const item = queue[next++] as Item;
        update(item.key, { state: 'uploading', progress: 0, message: '' });
        try {
          await customersApi.uploadCustomerDocument(customerId, item.file, item.slot, undefined, (fraction) =>
            update(item.key, { progress: fraction }),
          );
          filed++;
          update(item.key, { state: 'filed', progress: 1, message: 'Filed, awaiting review.' });
        } catch (err: unknown) {
          update(item.key, { state: 'failed', message: failureMessage(err) });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
    setRunning(false);
    if (filed > 0) onUploaded();
  };

  const count = (state: ItemState): number => items.filter((item) => item.state === state).length;
  const pending = count('ready') + count('failed');
  const close = (): void => {
    if (running) return;
    setOpen(false);
    setItems([]);
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
        Upload documents
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="md" aria-labelledby="bulk-upload-title">
        <DialogTitle id="bulk-upload-title">Upload documents</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box
              onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              data-testid="bulk-upload-drop"
              sx={{
                border: '1px dashed', borderColor: dragging ? 'primary.main' : 'divider', borderRadius: 1,
                p: 3, textAlign: 'center', bgcolor: dragging ? 'action.hover' : 'transparent',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Drop files or a folder here, or choose them. PDF, JPG, PNG, HEIC or WEBP, up to 10 MB
                each, {String(MAX_FILES)} per batch.
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <Button component="label" size="small" variant="outlined" disabled={running}>
                  Choose files
                  <input
                    type="file" hidden multiple accept={ACCEPT_ATTR} aria-label="Choose files"
                    onChange={(event) => { add([...(event.target.files ?? [])]); event.target.value = ''; }}
                  />
                </Button>
                <Button component="label" size="small" variant="outlined" disabled={running}>
                  Choose a folder
                  <input
                    type="file" hidden multiple aria-label="Choose a folder"
                    // Not in React's typings; every current browser supports it.
                    {...{ webkitdirectory: '', directory: '' }}
                    onChange={(event) => { add([...(event.target.files ?? [])]); event.target.value = ''; }}
                  />
                </Button>
              </Stack>
            </Box>

            {items.length > 0 ? (
              <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label="Files to upload">
                {items.map((item) => (
                  <Box component="li" key={item.key} aria-label={item.file.name}
                    sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' }, gap: 1, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={item.file.name}>{item.file.name}</Typography>
                      <Typography variant="caption" color={item.state === 'failed' || item.state === 'skipped' ? 'error' : 'text.secondary'} display="block">
                        {sizeLabel(item.file.size)}
                        {item.message !== '' ? ` · ${item.message}` : ''}
                        {item.state === 'uploading' ? ` · Uploading ${String(Math.round(item.progress * 100))}%` : ''}
                      </Typography>
                      {item.state === 'uploading' || item.state === 'filed' ? (
                        <LinearProgress variant="determinate" value={Math.round(item.progress * 100)}
                          color={item.state === 'filed' ? 'success' : 'primary'} sx={{ mt: 0.5 }}
                          aria-label={`${item.file.name} progress`} />
                      ) : null}
                    </Box>
                    {item.state === 'skipped' ? (
                      <Button size="small" onClick={() => setItems((prev) => prev.filter((other) => other.key !== item.key))}>
                        Remove from list
                      </Button>
                    ) : (
                      <TextField
                        select size="small" label="Document type" value={item.slot}
                        disabled={item.state === 'uploading' || item.state === 'filed' || running}
                        onChange={(event) => update(item.key, { slot: event.target.value })}
                      >
                        {slots.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                      </TextField>
                    )}
                  </Box>
                ))}
              </Stack>
            ) : null}

            {items.length > 0 && !running && count('filed') + count('failed') > 0 ? (
              <Alert severity={count('failed') > 0 ? 'warning' : 'success'} role="status">
                {String(count('filed'))} filed
                {count('failed') > 0 ? `, ${String(count('failed'))} failed — retry them or remove them` : ''}
                {count('skipped') > 0 ? `, ${String(count('skipped'))} not sent` : ''}.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={running}>{count('filed') > 0 ? 'Done' : 'Cancel'}</Button>
          <Button variant="contained" disabled={running || pending === 0} onClick={() => void run()}>
            {running ? 'Uploading…' : count('failed') > 0 && count('ready') === 0 ? `Retry ${String(pending)} failed` : `Upload ${String(pending)} file${pending === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
